import { BusinessObjectBase, decodeDecimals } from './base';
import { personalAccessTokenSchema } from '@schemas/business-objects/personal-access-token';
import type { User } from './user';
import type { Id } from '../base-types';
import { mintedPersonalAccessTokenSchema } from '@schemas/interface-definitions/minted-personal-access-token';
import { personalAccessTokenSummarySchema } from '@schemas/interface-definitions/personal-access-token-summary';
import type {
  MintedPersonalAccessToken,
  PersonalAccessTokenSummary,
} from '../interface-definitions/index';

export interface PersonalAccessTokenData {
  readonly id: Id;
  readonly name: string;
  readonly hash: string;
  readonly displayPrefix: string;
  readonly created?: Date;
  readonly expiresAt?: Date;
  readonly lastUsedAt?: Date;
  readonly revoked?: boolean;
  readonly userId: Id;
  readonly user?: User;
}

export interface PersonalAccessToken extends Readonly<PersonalAccessTokenData> {}
export class PersonalAccessToken extends BusinessObjectBase {
  static readonly entityName = 'PersonalAccessToken' as const;
  static readonly plural = 'personal-access-tokens';
  static schema = personalAccessTokenSchema;

  // --- Static CRUD methods ---

  static async find(_filter?: any): Promise<PersonalAccessToken[]> {
    throw new Error('find is not available on PersonalAccessToken');
  }

  static async findOne(_filter?: any): Promise<PersonalAccessToken | null> {
    throw new Error('findOne is not available on PersonalAccessToken');
  }

  static async findById(_id: Id, _filter?: any): Promise<PersonalAccessToken> {
    throw new Error('findById is not available on PersonalAccessToken');
  }

  static async create(_data: any): Promise<PersonalAccessToken> {
    throw new Error('create is not available on PersonalAccessToken');
  }

  static async createMany(_data: any[]): Promise<PersonalAccessToken[]> {
    throw new Error('createMany is not available on PersonalAccessToken');
  }

  static async createWithChildren(_data: any): Promise<PersonalAccessToken> {
    throw new Error(
      'createWithChildren is not available on PersonalAccessToken',
    );
  }

  static async createManyWithChildren(
    _data: any[],
  ): Promise<PersonalAccessToken[]> {
    throw new Error(
      'createManyWithChildren is not available on PersonalAccessToken',
    );
  }

  static async updateById(
    _id: Id,
    _updates: any,
  ): Promise<PersonalAccessToken> {
    throw new Error('updateById is not available on PersonalAccessToken');
  }

  static async deleteById(_id: Id): Promise<boolean> {
    throw new Error('deleteById is not available on PersonalAccessToken');
  }

  // --- Behaviors ---

  static async mine(): Promise<PersonalAccessTokenSummary[]> {
    const url = `${this.baseUrl}/${this.plural}/mine`;
    return this.get<PersonalAccessTokenSummary[]>(url).then((values) =>
      values?.map((value) =>
        decodeDecimals(personalAccessTokenSummarySchema, value),
      ),
    );
  }
  static async mint(
    name: string,
    expiresInDays?: number,
  ): Promise<MintedPersonalAccessToken> {
    const url = `${this.baseUrl}/${this.plural}/mint`;
    return this.post<MintedPersonalAccessToken>(url, {
      name,
      expiresInDays,
    }).then((value) => decodeDecimals(mintedPersonalAccessTokenSchema, value));
  }
  static async revoke(tokenId: Id): Promise<void> {
    const url = `${this.baseUrl}/${this.plural}/revoke`;
    return this.post<void>(url, { tokenId });
  }
}
